import { Sound, Scene, Vector3, Engine, Tools, Vector2, AbstractMesh } from "@babylonjs/core";
import { LowPanConfig, VolumeConfig, PanningConfig, DEFAULT_LOWPASS_CONFIG, DEFAULT_PANNING_CONFIG, DEFAULT_VOLUME_CONFIG, MeshAudioDetails, INVERTED_VOLUME_CONFIG, INVERTED_LOWPASS_CONFIG, MeshAngleInfo } from "./AudioConfig";
import { interpolate, smoothInterpolate } from "./mathUtils";
import _ from "lodash";
import { calculateMeshAudioDetailsGivenVectors } from "./userMeshUtils";


const constantSoundOptions = {
  autoplay: true,
  spatialSound: false,
  maxDistance: 1000,
}

const soundOptionsInverseModel = {
  autoplay: true,
  spatialSound: true,
  distanceModel: 'inverse',
  refDistance: 2.5,
  rolloffFactor: 1.25,
}

declare const window: any;

export type SoundMode = 'default' | 'inverted' | 'directional' | 'range' | 'focus';
export type DirectionalValue = 'left' | 'right';

export class UserSound {
  sound: Sound;
  lowPassFilter?: BiquadFilterNode;
  distanceGain?: GainNode;
  sourceAngleGain?: GainNode;
  targetAngleGain?: GainNode;
  panner?: StereoPannerNode;

  mode: SoundMode;
  direction?: DirectionalValue;
  range?: Vector2;
  user?: AbstractMesh;

  lowPassConfig: LowPanConfig = DEFAULT_LOWPASS_CONFIG;
  volumeConfig: VolumeConfig = DEFAULT_VOLUME_CONFIG;
  panningConfig: PanningConfig = DEFAULT_PANNING_CONFIG;


  constructor(id: string, streamOrSrc: MediaStream | string, scene: Scene) {
    this.sound = new Sound(`s-${id}`, streamOrSrc, scene, null, constantSoundOptions);
    this.sound.setLocalDirectionToMesh(new Vector3(0, 0, -0.1));
    this.mode = 'default';

    const audioContext = Engine.audioEngine?.audioContext;
    if (!audioContext) return;
  
    this.createLowPassFilter(audioContext);
    this.distanceGain = audioContext.createGain();
    this.sourceAngleGain = audioContext.createGain();
    this.targetAngleGain = audioContext.createGain();
    this.panner = audioContext.createStereoPanner()
  }

  public updateAudioFilters(sourcePosition: Vector3, sourceRotation: Vector3, targetPosition: Vector3, targetRotation: Vector3, options = { lowpass: true, distance: true, source: true, target: true, pan: true }) {
    const meshAudioDetails = calculateMeshAudioDetailsGivenVectors(sourcePosition, sourceRotation, targetPosition, targetRotation);
    if (options.lowpass) this.updateLowPassFilter(meshAudioDetails);
    if (options.distance) this.updateDistanceGain(meshAudioDetails);
    if (options.source) this.updateSourceAngleGain(meshAudioDetails);
    if (options.target) this.updateTargetAngleGain(meshAudioDetails);
    if (options.pan) this.updatePan(meshAudioDetails);
  }

  public logAudioFilters(s1p?: Vector3, s1r?: Vector3, tp?: Vector3, tr?: Vector3) {
    const mad1 = !s1p || !s1r || !tp || !tr ? undefined : calculateMeshAudioDetailsGivenVectors(s1p, s1r, tp, tr);
    if (this.sourceAngleGain && this.targetAngleGain && this.distanceGain) {
      const totalGain = this.distanceGain.gain.value * this.sourceAngleGain.gain.value * this.targetAngleGain.gain.value;
      const modeMsg = `${this.mode}${this.mode === 'directional' ? '-' + this.direction : this.mode === 'range' ? '-' + this.range : ''}`;
      const madMsg = !mad1 ? '' : `, dS: ${mad1.distance.toFixed(1)}, θS: ${Tools.ToDegrees(mad1.sourceToTarget.clockwiseAngleRadians).toFixed(1)}, `;
      console.log(`${this.sound.name}: ${modeMsg}${madMsg}lPF: ${this.lowPassFilter?.frequency.value.toFixed(0)}, FV: ${totalGain.toFixed(3)}, dV: ${this.distanceGain?.gain.value.toFixed(3)}, θV: ${this.sourceAngleGain?.gain.value.toFixed(3)}, tV: ${this.targetAngleGain?.gain.value.toFixed(3)} - pan: ${this.panner?.pan.value.toFixed(3)}`);
    }
  }

  private updateLowPassFilter(meshAudioDetails: MeshAudioDetails) {
    if (!this.lowPassFilter) return;
    switch(this.mode) {
      case 'default':
        this.lowPassFilter.frequency.value = this.defaultAndInvertedLowPassFn(meshAudioDetails);
      break;
      case 'inverted':
        this.lowPassFilter.frequency.value = this.defaultAndInvertedLowPassFn(meshAudioDetails);
      break;
      case 'directional':
        this.lowPassFilter.frequency.value = this.directionalLowPassFn(meshAudioDetails);
        break;
      case 'range':
        this.lowPassFilter.frequency.value = this.rangeLowPassFn(meshAudioDetails);
        break;
      case 'focus':
        // this.lowPassFilter.frequency.value = this.rangeLowPassFn(meshAudioDetails);
        break;
    }
  }

  private defaultAndInvertedLowPassFn({ distance, sourceToTarget, targetToSource}: MeshAudioDetails) {
    const config = this.mode === 'inverted' ? INVERTED_LOWPASS_CONFIG: DEFAULT_LOWPASS_CONFIG;
    if (!this.lowPassFilter) return config.maxValue;
    if (distance < config.minRange) {
      return config.maxValue;
    } else if(distance >= config.maxRange) {
      return config.minValue;
    } else {
      const transformDistanceAndInterpolateForRange = (distance: number) => {
        const effectiveInnerConeAngleRadians = distance < config.minRange ? config.source.innerConeAngleRadiansAtMinDistance : interpolate(distance, config.minRange, config.source.innerConeMaxDistance, config.source.innerConeAngleRadiansAtMinDistance, config.source.innerConeAngleRadiansAtMaxDistance);
        const amplificationForInnerCone = interpolate(distance, config.minRange, config.source.innerConeMaxDistance, config.source.innerConeMinAmplification, config.source.innerConeMaxAmplification);
        if (sourceToTarget.shortestAngleRadians < effectiveInnerConeAngleRadians && distance > config.minRange + amplificationForInnerCone) {
          distance -= amplificationForInnerCone;
        }
        if (sourceToTarget.shortestAngleRadians > effectiveInnerConeAngleRadians) {
          distance += interpolate(sourceToTarget.shortestAngleRadians, effectiveInnerConeAngleRadians, config.source.outerConeAngleRadians, config.source.outerConeMinReduction, config.source.outerConeMaxReduction);
        }
        if (targetToSource.shortestAngleRadians > config.target.innerConeAngleRadians) {
          distance += interpolate(targetToSource.shortestAngleRadians, config.target.innerConeAngleRadians, config.target.outerConeAngleRadians, config.target.outerConeMinReduction, config.target.outerConeMaxReduction);
        }
        let i = 0;
        while (config.valueAtDistance[i].x < distance && i < config.valueAtDistance.length - 1) {
          i++;
        }
        return i === 0 ? config.valueAtDistance[0].y : interpolate(distance, config.valueAtDistance[i-1].x, config.valueAtDistance[i].x, config.valueAtDistance[i-1].y, config.valueAtDistance[i].y);
      }
      const interpolVal = transformDistanceAndInterpolateForRange(distance);
      return _.clamp(interpolVal, this.lowPassFilter.frequency.minValue, this.lowPassFilter.frequency.maxValue);
    }
  }

  private directionalLowPassFn({ distance, sourceToTarget, targetToSource}: MeshAudioDetails) {
    if (!this.direction) return this.defaultAndInvertedLowPassFn({ distance, sourceToTarget, targetToSource });
    const padding = Math.PI / 12;
    const directionLeftButSufficientlyToTheRight = this.direction === 'left' && sourceToTarget.clockwiseAngleRadians < Math.PI - padding && sourceToTarget.clockwiseAngleRadians > padding;
    const directionRightButSufficientlyToTheLeft = this.direction === 'right' && sourceToTarget.clockwiseAngleRadians > Math.PI + padding && sourceToTarget.clockwiseAngleRadians < 2 * Math.PI - padding;
    if (directionLeftButSufficientlyToTheRight || directionRightButSufficientlyToTheLeft) return interpolate(sourceToTarget.shortestAngleRadians, padding, 2 * padding, 300, 40);
    return 24000;
  }

  private rangeLowPassFn({ distance, sourceToTarget, targetToSource}: MeshAudioDetails) {
    if (!this.range) return this.defaultAndInvertedLowPassFn({ distance, sourceToTarget, targetToSource });
    const zones = [24000, 900, 300, 100, 40];
    if (distance >= this.range.x && distance <= this.range.y) return zones[0];
    const distanceFromRange = Math.min(Math.abs(distance - this.range.x), Math.abs(distance - this.range.y));
    const distanceZone = _.clamp(Math.round(distanceFromRange / 5), 1, zones.length - 1);
    return zones[distanceZone];
  }

  private updateDistanceGain({ distance, sourceToTarget }: MeshAudioDetails) {
    if (!this.distanceGain) return;
    switch(this.mode) {
      case 'default':
        this.distanceGain.gain.value = this.defaultDistanceGainFn(distance);
        break;
      case 'inverted':
        this.distanceGain.gain.value = this.invertedDistanceGainFn(distance);
        break;
      case 'directional':
        this.distanceGain.gain.value = this.directionalDistanceGainFn(distance, sourceToTarget);
        break;
      case 'range':
        this.distanceGain.gain.value = this.rangeDistanceGainFn(distance);
        break;
      case 'focus':
        // this.distanceGain.gain.value = this.rangeDistanceGainFn(distance);
        break;
      default:
        this.distanceGain.gain.value = this.defaultDistanceGainFn(distance);
        break;
    }
  }

  private defaultDistanceGainFn(distance: number) {
    let effectiveDistance = Math.max(0, distance - this.volumeConfig.distanceMinRange);
    const updatedVolume = 1 / (1 + effectiveDistance * effectiveDistance * 0.33);
    return updatedVolume;
  }

  private invertedDistanceGainFn(distance: number) {
    const gainAtDistance = [new Vector2(0, 0.0025), new Vector2(5, 0.005), new Vector2(10, 0.01), new Vector2(15, 0.05), new Vector2(20, 0.1), new Vector2(25, 0.25), new Vector2(35, 0.5), new Vector2(100, 0.7), new Vector2(200, 0.9)];
    distance = _.clamp(distance, gainAtDistance[0].x, gainAtDistance[gainAtDistance.length-1].x);
    let i=0;
    while (gainAtDistance[i].x <= distance) i++;
    const newGainValue = interpolate(distance, gainAtDistance[i-1].x, gainAtDistance[i].x, gainAtDistance[i-1].y, gainAtDistance[i].y);
    return newGainValue;  
  }

  private directionalDistanceGainFn(distance: number, angleRadians: MeshAngleInfo) {
    if(!this.direction) return this.defaultDistanceGainFn(distance);
    const padding = Math.PI / 12;
    const directionLeftButSufficientlyToTheRight = this.direction === 'left' && angleRadians.clockwiseAngleRadians < Math.PI - padding && angleRadians.clockwiseAngleRadians > padding;
    const directionRightButSufficientlyToTheLeft = this.direction === 'right' && angleRadians.clockwiseAngleRadians > Math.PI + padding && angleRadians.clockwiseAngleRadians < 2 * Math.PI - padding;
    if (directionLeftButSufficientlyToTheRight || directionRightButSufficientlyToTheLeft) distance = 5;
    return this.invertedDistanceGainFn(distance);
  }

  private rangeDistanceGainFn(distance: number) {
    if (!this.range) return this.defaultDistanceGainFn(distance);
    const zoneGain = 0.8;
    const minGain = 0.005;
    const distanceFromZoneToMinGain = 5;
    if (distance >= this.range.x && distance <= this.range.y) return zoneGain;
    const distanceFromRange = Math.min(Math.abs(distance - this.range.x), Math.abs(distance - this.range.y));
    return interpolate(distanceFromRange, 0, distanceFromZoneToMinGain, zoneGain, minGain);
  }

  private updateSourceAngleGain({ distance, sourceToTarget }: MeshAudioDetails) {
    if (!this.sourceAngleGain) return;
    const config = this.volumeConfig.sourceAngleConfig;
    const distanceMinRange = this.volumeConfig.distanceMinRange + 1;
    let effectiveDistance = Math.max(0, distance - distanceMinRange);
    const effectiveInnerConeAngleRadians = effectiveDistance < 0 ? config.innerConeAngleRadiansAtMinDistance : interpolate(distance, distanceMinRange, config.innerConeMaxDistance, config.innerConeAngleRadiansAtMinDistance, config.innerConeAngleRadiansAtMaxDistance);
  
    const isClose = distance <= distanceMinRange;
    const isFacingSource = sourceToTarget.shortestAngleRadians <= effectiveInnerConeAngleRadians
  
    if (isClose && isFacingSource) this.sourceAngleGain.gain.value = 1;
    else if (isClose) this.sourceAngleGain.gain.value = interpolate(distance, 0, distanceMinRange, 0.6, 1);
    else if (!isClose && isFacingSource) {
      const amplification = interpolate(distance, distanceMinRange, config.innerConeMaxDistance, config.innerConeMinAmplification, config.innerConeMaxAmplification);
      const bufferCoefficient = 0.2;
      const bufferForInnerCone = interpolate(distance, distanceMinRange, config.innerConeMaxDistance, config.innerConeAngleRadiansAtMinDistance * bufferCoefficient, config.innerConeAngleRadiansAtMaxDistance * bufferCoefficient);
      const smoothAmpllification = interpolate(sourceToTarget.shortestAngleRadians, effectiveInnerConeAngleRadians - bufferForInnerCone, effectiveInnerConeAngleRadians, amplification, 0);
      this.sourceAngleGain.gain.value = 1 + smoothAmpllification;
    }
    else if (!isFacingSource) {
      let interpolationValue = smoothInterpolate(sourceToTarget.shortestAngleRadians, effectiveInnerConeAngleRadians, config.outerConeAngleRadians, config.outerConeMinReduction, config.outerConeMaxReduction, effectiveInnerConeAngleRadians + config.outerConeBufferForMinValue, 0);
      this.sourceAngleGain.gain.value = 1 - interpolationValue;
    }
  }

  private updateTargetAngleGain({ distance, targetToSource }: MeshAudioDetails) {
    if (!this.targetAngleGain) return;
    const config = this.volumeConfig.targetAngleConfig;
    const isFacingSource = targetToSource.shortestAngleRadians <= config.innerConeAngleRadians || distance <= 0.2;
  
    if (isFacingSource) this.targetAngleGain.gain.value = 1;
    else if (!isFacingSource) {
      let interpolationValue = smoothInterpolate(targetToSource.shortestAngleRadians, config.innerConeAngleRadians, config.outerConeAngleRadians, config.outerConeMinReduction, config.outerConeMaxReduction, config.innerConeAngleRadians + config.outerConeBufferForMinValue, 0);
      this.targetAngleGain.gain.value = 1 - interpolationValue;
    }
  }

  private updatePan({ distance, sourceToTarget }: MeshAudioDetails) {
    if (!this.panner) return;
    const config = this.panningConfig;
    const effectiveDistance = Math.max(0, distance - config.minRange);
    const gradient = (config.maxValue - config.minValue) / (config.maxRange - config.minRange);
    const panningCoefficient = Math.min(config.maxValue, gradient * effectiveDistance + config.minValue); // y = mx + c with a ceiling
    const panValue = Math.sin(sourceToTarget.clockwiseAngleRadians) * panningCoefficient;
    // console.log('distance: ' , distance, ' pan: ' + attemptOne + ' -- ');
    this.panner.pan.value = panValue;
  }

  // TODO: Clean up config logic (invert should take maxDistance into account)
  public updateMode(mode: SoundMode, options?: { direction?: DirectionalValue, range?: Vector2, user?: AbstractMesh }) {
    if (mode === 'inverted') {
      this.mode = 'inverted';
      this.volumeConfig = INVERTED_VOLUME_CONFIG;
      this.lowPassConfig = INVERTED_LOWPASS_CONFIG;
    }
    else if (mode === 'directional' && !!options && !!options.direction) {
      this.mode = 'directional';
      this.direction = options.direction;
      this.volumeConfig = DEFAULT_VOLUME_CONFIG;
      this.lowPassConfig = DEFAULT_LOWPASS_CONFIG;
    }
    else if (mode === 'range' && !!options?.range && options.range.x < options.range.y) {
      this.mode = 'range';
      this.range = options.range;
      this.volumeConfig = DEFAULT_VOLUME_CONFIG;
      this.volumeConfig.sourceAngleConfig.innerConeMinAmplification = 0.05;
      this.volumeConfig.sourceAngleConfig.innerConeMaxAmplification = 0.20;
      this.lowPassConfig = DEFAULT_LOWPASS_CONFIG;
    }
    else if (mode === 'focus' && !!options && options.user) {
      this.mode = 'focus';
      this.user = options.user;
      this.volumeConfig = DEFAULT_VOLUME_CONFIG;
      this.lowPassConfig = DEFAULT_LOWPASS_CONFIG;
    }
    else {
      this.mode = 'default';
      this.volumeConfig = DEFAULT_VOLUME_CONFIG;
      this.lowPassConfig = DEFAULT_LOWPASS_CONFIG;
    }
  }

  public applyAudioFilters() {
    const audioContext = Engine.audioEngine?.audioContext;
    if (!audioContext) return;
    if (this.lowPassFilter && this.distanceGain && this.sourceAngleGain && this.targetAngleGain && this.panner) {
      const destination = audioContext.destination;
      
      this.sound.connectToSoundTrackAudioNode(this.distanceGain);
      this.distanceGain.connect(this.sourceAngleGain);
      this.sourceAngleGain.connect(this.targetAngleGain);
      this.targetAngleGain.connect(this.lowPassFilter);
      this.lowPassFilter.connect(this.panner);
      this.panner.connect(destination);
  
      if (!window.sounds) window.sounds = {};
      window.sounds[this.sound.name] = this;
    } else {
      this.removeAudioFilters();
    }
  }

  public disconnect() {
    this.removeAudioFilters();
    this.sound.stop();
    this.sound.detachFromMesh();
  }

  private createLowPassFilter(audioContext: AudioContext) {
    this.lowPassFilter = audioContext.createBiquadFilter();
    this.lowPassFilter.type = 'lowpass';
    this.lowPassFilter.frequency.value = this.lowPassFilter.frequency.maxValue;
  }

  // TODO: If audioContext is not found, we should recreate sound source with auto spatial settings?
  private removeAudioFilters() {
    const audioContext = Engine.audioEngine?.audioContext;
    if (audioContext) {
      this.sound.connectToSoundTrackAudioNode(audioContext.destination);
    }
  }
}
